import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import InvoicePage from './InvoicePage';
import { useIsMobile } from '../hooks/useIsMobile';

const T = {
  bg: '#f2f2ee',
  border: '#e5e0d5',
  text: '#0A0A0A',
  muted: '#8b7d6b',
  red: '#c0392b',
};
const SZ   = 'clamp(9px, 0.9vw, 13px)';
const FONT = "'SuisseSemiBold'";

const S = {
  label: { fontSize: SZ, letterSpacing: '0.06em', color: T.muted, textTransform: 'uppercase', display: 'block', marginBottom: 8 },
  input: { width: '100%', background: T.bg, border: `1px solid ${T.border}`, color: T.text, fontSize: 13, padding: '10px 12px', outline: 'none', fontFamily: FONT, boxSizing: 'border-box', marginBottom: 14 },
  btn:   { background: T.text, color: '#fff', border: 'none', padding: '10px 20px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT },
  ghost: { background: 'transparent', color: T.muted, border: `1px solid ${T.border}`, padding: '9px 16px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT },
  del:   { background: 'transparent', color: T.red, border: 'none', padding: '8px 10px', fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase', cursor: 'pointer', fontFamily: FONT },
  row:   { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: `1px solid ${T.border}` },
  modal: { position: 'fixed', inset: 0, background: 'rgba(242,242,238,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, backdropFilter: 'blur(2px)' },
  mbox:  { background: T.bg, border: `1px solid ${T.border}`, padding: 32, width: '100%', maxWidth: 420, margin: '0 16px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
};

function ModalLabel({ children }) {
  return <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: T.muted, textTransform: 'uppercase', marginBottom: 24 }}>{children}</div>;
}

function Dot() {
  return <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: T.text, marginRight: 8, verticalAlign: 'middle', marginBottom: 2 }} />;
}

// ── Modals ────────────────────────────────────────────────────────────────────

function PasswordField({ value, onChange, label = 'Password', required = true }) {
  const [show, setShow] = useState(false);
  return (
    <>
      <label style={S.label}>{label}</label>
      <div style={{ position: 'relative', marginBottom: 14 }}>
        <input
          style={{ ...S.input, marginBottom: 0, paddingRight: 44 }}
          type={show ? 'text' : 'password'}
          value={value}
          onChange={onChange}
          required={required}
          minLength={6}
          autoComplete="new-password"
        />
        <button
          type="button"
          onClick={() => setShow(s => !s)}
          style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 11, color: T.muted, fontFamily: FONT, letterSpacing: '0.06em', padding: 0 }}
        >
          {show ? 'hide' : 'show'}
        </button>
      </div>
    </>
  );
}

function CreateClientModal({ onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try { onCreated(await api.users.create({ username: email, password, display_name: displayName || email })); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.mbox}>
        <ModalLabel>New Client</ModalLabel>
        <form onSubmit={submit}>
          <label style={S.label}>Display Name</label>
          <input style={S.input} value={displayName} onChange={e => setDisplayName(e.target.value)} required autoFocus />
          <label style={S.label}>Email (Login)</label>
          <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="off" />
          <PasswordField value={password} onChange={e => setPassword(e.target.value)} />
          {error && <div style={{ fontSize: SZ, color: T.red, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="submit" disabled={saving} style={S.btn}>{saving ? 'Creating...' : 'Create'}</button>
            <button type="button" onClick={onClose} style={S.ghost}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function AddAuthorizedUserModal({ client, onClose, onCreated }) {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try { onCreated(await api.users.createAuthorized(client.id, { email, password, display_name: displayName || email })); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.mbox}>
        <ModalLabel>Add Authorized User — {client.display_name}</ModalLabel>
        <form onSubmit={submit}>
          <label style={S.label}>Display Name</label>
          <input style={S.input} value={displayName} onChange={e => setDisplayName(e.target.value)} autoFocus />
          <label style={S.label}>Email (Login)</label>
          <input style={S.input} type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="off" />
          <PasswordField value={password} onChange={e => setPassword(e.target.value)} />
          {error && <div style={{ fontSize: SZ, color: T.red, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="submit" disabled={saving} style={S.btn}>{saving ? 'Adding...' : 'Add User'}</button>
            <button type="button" onClick={onClose} style={S.ghost}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CreateBriefModal({ onClose, onCreated, clients }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [clientId, setClientId] = useState('');
  const [shootDate, setShootDate] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try {
      onCreated(await api.briefs.create({ title, description, client_id: clientId ? Number(clientId) : null, shoot_date: shootDate }));
    } catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.mbox}>
        <ModalLabel>New Brief</ModalLabel>
        <form onSubmit={submit}>
          <label style={S.label}>Title</label>
          <input style={S.input} value={title} onChange={e => setTitle(e.target.value)} required autoFocus />
          <label style={S.label}>Description (optional)</label>
          <input style={S.input} value={description} onChange={e => setDescription(e.target.value)} />
          <label style={S.label}>Shoot Date (optional)</label>
          <input style={S.input} value={shootDate} onChange={e => setShootDate(e.target.value)} placeholder="e.g. June 2026" />
          <label style={S.label}>Assign to Client (optional)</label>
          <select style={{ ...S.input, marginBottom: 20 }} value={clientId} onChange={e => setClientId(e.target.value)}>
            <option value="">— Unassigned —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.display_name || c.username}</option>)}
          </select>
          {error && <div style={{ fontSize: SZ, color: T.red, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10 }}>
            <button type="submit" disabled={saving} style={S.btn}>{saving ? 'Creating...' : 'Create'}</button>
            <button type="button" onClick={onClose} style={S.ghost}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordModal({ client, onClose }) {
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true);
    try { await api.users.resetPassword(client.id, password); setDone(true); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.mbox}>
        <ModalLabel>Reset Password — {client.display_name || client.username}</ModalLabel>
        {done ? (
          <>
            <p style={{ fontSize: SZ, color: T.muted, marginBottom: 24, letterSpacing: '0.06em' }}>Password updated.</p>
            <button onClick={onClose} style={S.btn}>Done</button>
          </>
        ) : (
          <form onSubmit={submit}>
            <PasswordField value={password} onChange={e => setPassword(e.target.value)} label="New Password" />
            {error && <div style={{ fontSize: SZ, color: T.red, marginBottom: 14 }}>{error}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" disabled={saving} style={S.btn}>{saving ? 'Updating...' : 'Update'}</button>
              <button type="button" onClick={onClose} style={S.ghost}>Cancel</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// ── Sections ──────────────────────────────────────────────────────────────────

function BriefsSection({ clients }) {
  const navigate = useNavigate();
  const [briefs, setBriefs] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  useEffect(() => { api.briefs.list().then(setBriefs).catch(console.error); }, []);

  async function deleteBrief(id) {
    if (!window.confirm('Delete this brief?')) return;
    await api.briefs.delete(id);
    setBriefs(prev => prev.filter(b => b.id !== id));
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowCreate(true)} style={S.btn}>+ New Brief</button>
      </div>
      {briefs.length === 0
        ? <p style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.06em', padding: '12px 0' }}>No briefs yet.</p>
        : briefs.map(brief => (
          <div key={brief.id} style={S.row}>
            <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
              <div style={{ fontSize: SZ, color: T.text, letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{brief.title}</div>
              <div style={{ fontSize: SZ, letterSpacing: '0.06em', color: T.muted, textTransform: 'uppercase' }}>
                {brief.client_name || 'Unassigned'}{brief.shoot_date ? ` · ${brief.shoot_date}` : ''}
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
              <button onClick={() => navigate(`/admin/briefs/${brief.id}`)} style={S.ghost}>Open</button>
              <button onClick={() => deleteBrief(brief.id)} style={S.del}>Delete</button>
            </div>
          </div>
        ))
      }
      {showCreate && (
        <CreateBriefModal
          clients={clients}
          onClose={() => setShowCreate(false)}
          onCreated={b => { setBriefs(prev => [b, ...prev]); setShowCreate(false); }}
        />
      )}
    </div>
  );
}

function ClientsSection() {
  const [clients, setClients] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [resettingUser, setResettingUser] = useState(null);
  const [addingAuthTo, setAddingAuthTo] = useState(null);
  const [expanded, setExpanded] = useState(new Set());

  useEffect(() => { api.users.list().then(setClients).catch(console.error); }, []);

  function toggleExpand(id) {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  async function deleteClient(id) {
    if (!window.confirm('Delete this client and all their authorized users? Briefs will become unassigned.')) return;
    await api.users.delete(id);
    setClients(prev => prev.filter(c => c.id !== id));
  }

  async function deleteAuthorized(clientId, userId) {
    if (!window.confirm('Remove this authorized user?')) return;
    await api.users.delete(userId);
    setClients(prev => prev.map(c =>
      c.id === clientId
        ? { ...c, authorized_users: c.authorized_users.filter(u => u.id !== userId) }
        : c
    ));
  }

  function handleClientCreated(u) {
    const newClient = { ...u, authorized_users: [] };
    setClients(prev => [newClient, ...prev]);
    setShowCreate(false);
    setAddingAuthTo(newClient); // immediately open add-user modal for the new client
  }

  function handleAuthCreated(clientId, u) {
    setClients(prev => prev.map(c =>
      c.id === clientId ? { ...c, authorized_users: [...c.authorized_users, u] } : c
    ));
    setAddingAuthTo(null);
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button onClick={() => setShowCreate(true)} style={S.btn}>+ New Client</button>
      </div>

      {clients.length === 0
        ? <p style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.06em', padding: '12px 0' }}>No clients yet.</p>
        : clients.map(client => {
          const isOpen = expanded.has(client.id);
          const authCount = client.authorized_users?.length ?? 0;
          return (
            <div key={client.id} style={{ borderBottom: `1px solid ${T.border}` }}>

              {/* Client row — click name area to expand */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0' }}>
                <button
                  onClick={() => toggleExpand(client.id)}
                  style={{ flex: 1, minWidth: 0, paddingRight: 12, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, fontFamily: FONT }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: SZ, color: T.text, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {client.display_name || client.username}
                    </span>
                    {authCount > 0 && (
                      <span style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.04em' }}>
                        {isOpen ? '▲' : '▼'} {authCount}
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: SZ, letterSpacing: '0.04em', color: T.muted }}>{client.username}</div>
                </button>
                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                  <button onClick={() => setAddingAuthTo(client)} style={S.ghost}>+ User</button>
                  <button onClick={() => setResettingUser(client)} style={S.ghost}>Reset</button>
                  <button onClick={() => deleteClient(client.id)} style={S.del}>Delete</button>
                </div>
              </div>

              {/* Authorized users — accordion */}
              {isOpen && authCount > 0 && (
                <div style={{ marginBottom: 12, paddingLeft: 16, borderLeft: `2px solid ${T.border}` }}>
                  {client.authorized_users.map(u => (
                    <div key={u.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 0', borderBottom: `1px solid ${T.border}` }}>
                      <div>
                        {u.display_name && u.display_name !== u.username && (
                          <div style={{ fontSize: SZ, color: T.text, letterSpacing: '0.06em', marginBottom: 2 }}>{u.display_name}</div>
                        )}
                        <div style={{ fontSize: SZ, letterSpacing: '0.04em', color: T.muted }}>{u.username}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <button onClick={() => setResettingUser(u)} style={S.ghost}>Reset</button>
                        <button onClick={() => deleteAuthorized(client.id, u.id)} style={S.del}>Remove</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })
      }

      {showCreate && (
        <CreateClientModal onClose={() => setShowCreate(false)} onCreated={handleClientCreated} />
      )}
      {addingAuthTo && (
        <AddAuthorizedUserModal
          client={addingAuthTo}
          onClose={() => setAddingAuthTo(null)}
          onCreated={u => handleAuthCreated(addingAuthTo.id, u)}
        />
      )}
      {resettingUser && (
        <ResetPasswordModal client={resettingUser} onClose={() => setResettingUser(null)} />
      )}
    </div>
  );
}

// ── Projects ──────────────────────────────────────────────────────────────────

function slugify(s) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
}

function resolveImg(src) {
  return src.startsWith('/uploads/') ? `${api.BASE_URL}${src}` : src;
}

// ── Focal point picker ────────────────────────────────────────────────────────

function FocalPointModal({ img, onClose, onSaved }) {
  const src = resolveImg(img.src);
  const [focal, setFocal] = useState(img.focal_point || 'center center');
  const [saving, setSaving] = useState(false);

  // Parse "X% Y%" → { x, y } numbers; handle keyword values too
  function parseFocal(fp) {
    const parts = (fp || 'center center').trim().split(/\s+/);
    const toNum = v => {
      if (v === 'left')   return 0;
      if (v === 'center') return 50;
      if (v === 'right')  return 100;
      if (v === 'top')    return 0;
      if (v === 'bottom') return 100;
      return parseFloat(v) || 50;
    };
    return { x: toNum(parts[0] ?? '50%'), y: toNum(parts[1] ?? '50%') };
  }

  const { x, y } = parseFocal(focal);

  function handleImageClick(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const nx = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    const ny = Math.round(((e.clientY - rect.top) / rect.height) * 100);
    setFocal(`${nx}% ${ny}%`);
  }

  async function save() {
    setSaving(true);
    try { await onSaved(focal); onClose(); }
    catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.mbox, maxWidth: 560, width: '100%' }}>
        <ModalLabel>Set Crop Focus</ModalLabel>

        <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start', marginBottom: 20 }}>
          {/* Clickable full image */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: FONT, marginBottom: 6 }}>Click to set focus</div>
            <div
              onClick={handleImageClick}
              style={{ position: 'relative', cursor: 'crosshair', display: 'inline-block', maxWidth: '100%', border: `1px solid ${T.border}` }}
            >
              <img src={src} alt="" style={{ display: 'block', maxWidth: 300, maxHeight: 340, width: 'auto', height: 'auto' }} />
              {/* Focal point dot */}
              <div style={{
                position: 'absolute',
                left: `${x}%`, top: `${y}%`,
                transform: 'translate(-50%, -50%)',
                width: 14, height: 14,
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.9)',
                border: '2px solid rgba(68,68,67,0.8)',
                boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                pointerEvents: 'none',
              }} />
              {/* Crosshair lines */}
              <div style={{ position: 'absolute', left: `${x}%`, top: 0, bottom: 0, width: 1, background: 'rgba(255,255,255,0.4)', transform: 'translateX(-50%)', pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: `${y}%`, left: 0, right: 0, height: 1, background: 'rgba(255,255,255,0.4)', transform: 'translateY(-50%)', pointerEvents: 'none' }} />
            </div>
          </div>

          {/* Live 4:5 crop preview */}
          <div style={{ flexShrink: 0 }}>
            <div style={{ fontSize: 10, color: T.muted, letterSpacing: '0.06em', textTransform: 'uppercase', fontFamily: FONT, marginBottom: 6 }}>4×5 Preview</div>
            <div style={{ width: 100, aspectRatio: '4/5', overflow: 'hidden', border: `1px solid ${T.border}` }}>
              <img src={src} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: focal }} />
            </div>
            <div style={{ fontSize: 9, color: T.muted, fontFamily: FONT, marginTop: 6, letterSpacing: '0.04em' }}>{focal}</div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={save} disabled={saving} style={S.btn}>{saving ? 'Saving...' : 'Save'}</button>
          <button onClick={onClose} style={S.ghost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ── Library panel ─────────────────────────────────────────────────────────────

function LibraryPanel({ onClose, onDragStart }) {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dropping, setDropping] = useState(false);

  useEffect(() => {
    api.media.list()
      .then(setImages)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  async function uploadFile(file) {
    if (!file?.type.startsWith('image/')) return;
    setUploading(true);
    try {
      const result = await api.media.upload(file);
      setImages(prev => [result, ...prev]);
    } catch (err) { alert(err.message); }
    finally { setUploading(false); setDropping(false); }
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(242,242,238,0.5)', zIndex: 90, backdropFilter: 'blur(1px)' }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 280,
        background: '#f2f2ee', borderLeft: `1px solid #e5e0d5`,
        zIndex: 91, display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 16px', borderBottom: `1px solid #e5e0d5`, flexShrink: 0 }}>
          <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: T.muted, textTransform: 'uppercase', fontFamily: FONT }}>Library</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: SZ, color: T.muted, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase', padding: 0 }}>Close</button>
        </div>

        {/* Upload zone */}
        <label
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '12px 16px 0', border: `1px dashed ${dropping ? T.text : T.border}`, padding: '10px 8px', cursor: 'pointer', transition: 'border-color 0.15s', flexShrink: 0 }}
          onDragOver={e => { e.preventDefault(); setDropping(true); }}
          onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDropping(false); }}
          onDrop={e => { e.preventDefault(); setDropping(false); Array.from(e.dataTransfer.files).forEach(uploadFile); }}
        >
          <input type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => Array.from(e.target.files).forEach(uploadFile)} />
          <span style={{ fontSize: SZ, letterSpacing: '0.06em', color: dropping ? T.text : T.muted, textTransform: 'uppercase', fontFamily: FONT }}>
            {uploading ? 'Uploading...' : dropping ? 'Drop to upload' : '+ Upload to Library'}
          </span>
        </label>

        <p style={{ fontSize: 10, color: T.muted, letterSpacing: '0.04em', fontFamily: FONT, padding: '8px 16px 12px', flexShrink: 0, lineHeight: 1.5 }}>
          Drag an image onto an open project to add it.
        </p>

        {/* Image grid */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '0 16px 16px' }}>
          {loading ? (
            <p style={{ fontSize: SZ, color: T.muted, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Loading...</p>
          ) : images.length === 0 ? (
            <p style={{ fontSize: SZ, color: T.muted, fontFamily: FONT, letterSpacing: '0.06em', textTransform: 'uppercase' }}>No images yet.</p>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
              {images.map(img => (
                <div
                  key={img.key}
                  draggable
                  onDragStart={e => {
                    e.dataTransfer.setData('application/r2-image', JSON.stringify({ r2_key: img.key, original_filename: '' }));
                    e.dataTransfer.effectAllowed = 'copy';
                    onDragStart();
                  }}
                  style={{ border: `1px solid ${T.border}`, cursor: 'grab', position: 'relative' }}
                >
                  <img
                    src={`${api.BASE_URL}${img.src}`}
                    alt=""
                    style={{ width: '100%', aspectRatio: '1/1', objectFit: 'cover', display: 'block', pointerEvents: 'none' }}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onCancel()}>
      <div style={S.mbox}>
        <ModalLabel>Confirm</ModalLabel>
        <p style={{ fontSize: SZ, color: T.text, letterSpacing: '0.06em', lineHeight: 1.7, marginBottom: 28 }}>{message}</p>
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onConfirm} style={{ ...S.btn, background: T.red }}>Delete</button>
          <button onClick={onCancel} style={S.ghost}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

function SlugPreview({ slug }) {
  if (!slug) return null;
  return (
    <div style={{ fontSize: 11, color: T.muted, letterSpacing: '0.04em', marginTop: -10, marginBottom: 14, fontFamily: FONT }}>
      piercebtownsend.com/{slug}
    </div>
  );
}

function CreateProjectModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [client, setClient] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  function handleNameChange(e) {
    setName(e.target.value);
    if (!slugTouched) setSlug(slugify(e.target.value));
  }

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try { onCreated(await api.projects.create({ name, client, slug })); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.mbox}>
        <ModalLabel>New Project</ModalLabel>
        <form onSubmit={submit}>
          <label style={S.label}>Title</label>
          <input style={S.input} value={name} onChange={handleNameChange} required autoFocus />
          <label style={S.label}>Client</label>
          <input style={S.input} value={client} onChange={e => setClient(e.target.value)} placeholder="e.g. ON RUNNING" />
          <label style={S.label}>Slug</label>
          <input style={S.input} value={slug} onChange={e => { setSlug(e.target.value); setSlugTouched(true); }} required placeholder="url-friendly-id" />
          <SlugPreview slug={slug} />
          {error && <div style={{ fontSize: SZ, color: T.red, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="submit" disabled={saving} style={S.btn}>{saving ? 'Creating...' : 'Create'}</button>
            <button type="button" onClick={onClose} style={S.ghost}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditProjectModal({ project, onClose, onSaved }) {
  const [name, setName] = useState(project.name);
  const [client, setClient] = useState(project.client);
  const [slug, setSlug] = useState(project.slug);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  async function submit(e) {
    e.preventDefault(); setSaving(true); setError('');
    try { onSaved(await api.projects.update(project.id, { name, client, slug })); }
    catch (err) { setError(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={S.modal} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={S.mbox}>
        <ModalLabel>Edit Project</ModalLabel>
        <form onSubmit={submit}>
          <label style={S.label}>Title</label>
          <input style={S.input} value={name} onChange={e => setName(e.target.value)} required autoFocus />
          <label style={S.label}>Client</label>
          <input style={S.input} value={client} onChange={e => setClient(e.target.value)} />
          <label style={S.label}>Slug</label>
          <input style={S.input} value={slug} onChange={e => setSlug(e.target.value)} required />
          <SlugPreview slug={slug} />
          {error && <div style={{ fontSize: SZ, color: T.red, marginBottom: 14 }}>{error}</div>}
          <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
            <button type="submit" disabled={saving} style={S.btn}>{saving ? 'Saving...' : 'Save'}</button>
            <button type="button" onClick={onClose} style={S.ghost}>Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ProjectsSection() {
  const isMobile = useIsMobile();
  const [projects, setProjects] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(new Set());
  const [uploading, setUploading] = useState({});   // { [projectId]: { current, total } | null }
  const [confirm, setConfirm] = useState(null);      // { message, onConfirm }
  const [saved, setSaved] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [editingCrop, setEditingCrop] = useState(null); // image object
  const [libraryDragging, setLibraryDragging] = useState(false); // library drag in progress
  const [libDropTarget, setLibDropTarget] = useState(null);      // projectId being hovered

  // Project drag state
  const [draggingProject, setDraggingProject] = useState(null);
  const [dragOverProject, setDragOverProject] = useState(null);

  // Image drag state
  const [draggingImg, setDraggingImg] = useState(null);  // { projectId, idx }
  const [dragOverImg, setDragOverImg] = useState(null);   // { projectId, idx }

  const fileInputRef = useRef(null);
  const uploadTarget = useRef(null);

  useEffect(() => { api.projects.listAll().then(setProjects).catch(console.error); }, []);

  useEffect(() => {
    const clear = () => { setLibraryDragging(false); setLibDropTarget(null); };
    document.addEventListener('dragend', clear);
    return () => document.removeEventListener('dragend', clear);
  }, []);

  function flash() {
    setSaved(true);
    setTimeout(() => setSaved(false), 1600);
  }

  function askConfirm(message, onConfirm) {
    setConfirm({ message, onConfirm });
  }

  function toggleExpand(id) {
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  }

  async function toggleEnabled(project) {
    try {
      const updated = await api.projects.update(project.id, { enabled: !project.enabled });
      setProjects(prev => prev.map(p => p.id === updated.id ? updated : p));
    } catch (err) { alert(err.message); }
  }

  // ── Project drag-to-reorder ───────────────────────────────────────────────

  async function onProjectDragEnd() {
    const from = draggingProject;
    const to = dragOverProject;
    setDraggingProject(null);
    setDragOverProject(null);
    if (from === null || to === null || from === to) return;
    const list = [...projects];
    const [moved] = list.splice(from, 1);
    list.splice(to, 0, moved);
    setProjects(list);
    try { await api.projects.reorder(list.map(p => p.id)); flash(); }
    catch (err) { alert(err.message); }
  }

  async function deleteProject(id) {
    await api.projects.delete(id);
    setProjects(prev => prev.filter(p => p.id !== id));
  }

  // ── Image upload ──────────────────────────────────────────────────────────

  function triggerUpload(projectId) {
    uploadTarget.current = projectId;
    fileInputRef.current.value = '';
    fileInputRef.current.click();
  }

  async function handleFiles(e) {
    const projectId = uploadTarget.current;
    const files = Array.from(e.target.files);
    if (!files.length) return;
    for (let i = 0; i < files.length; i++) {
      setUploading(prev => ({ ...prev, [projectId]: { current: i + 1, total: files.length } }));
      try {
        const img = await api.projects.uploadImage(projectId, files[i]);
        setProjects(prev => prev.map(p =>
          p.id === projectId ? { ...p, images: [...p.images, img] } : p
        ));
      } catch (err) { alert(err.message); break; }
    }
    setUploading(prev => ({ ...prev, [projectId]: null }));
  }

  async function deleteImage(projectId, imgId) {
    await api.projects.deleteImage(projectId, imgId);
    setProjects(prev => prev.map(p =>
      p.id === projectId ? { ...p, images: p.images.filter(i => i.id !== imgId) } : p
    ));
  }

  // ── Image drag-to-reorder ─────────────────────────────────────────────────

  async function onImageDragEnd(projectId) {
    const from = draggingImg?.idx;
    const to = dragOverImg?.idx;
    setDraggingImg(null);
    setDragOverImg(null);
    if (from == null || to == null || from === to) return;
    const project = projects.find(p => p.id === projectId);
    const imgs = [...project.images];
    const [moved] = imgs.splice(from, 1);
    imgs.splice(to, 0, moved);
    setProjects(prev => prev.map(p => p.id === projectId ? { ...p, images: imgs } : p));
    try { await api.projects.reorderImages(projectId, imgs.map(i => i.id)); flash(); }
    catch (err) { alert(err.message); }
  }

  async function handleLibraryDrop(e, projectId) {
    e.preventDefault();
    setLibDropTarget(null);
    setLibraryDragging(false);
    const raw = e.dataTransfer.getData('application/r2-image');
    if (!raw) return;
    const { r2_key, original_filename } = JSON.parse(raw);
    try {
      const img = await api.projects.addFromLibrary(projectId, r2_key, original_filename);
      setProjects(prev => prev.map(p =>
        p.id === projectId ? { ...p, images: [...p.images, img] } : p
      ));
      flash();
    } catch (err) { alert(err.message); }
  }

  return (
    <div>
      <input ref={fileInputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={handleFiles} />

      {/* Saved flash toast */}
      <div style={{
        position: 'fixed', bottom: 28, right: 28, zIndex: 200,
        background: T.text, color: '#fff',
        padding: '9px 18px', fontSize: SZ, letterSpacing: '0.08em',
        fontFamily: FONT, textTransform: 'uppercase',
        opacity: saved ? 1 : 0, transition: 'opacity 0.25s ease',
        pointerEvents: 'none',
      }}>Saved ✓</div>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginBottom: 24 }}>
        <button onClick={() => setShowLibrary(v => !v)} style={showLibrary ? S.btn : S.ghost}>Library</button>
        <button onClick={() => setShowCreate(true)} style={S.btn}>+ New Project</button>
      </div>

      {projects.length === 0
        ? <p style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.06em', padding: '12px 0' }}>No projects yet.</p>
        : projects.map((project, idx) => {
          const isExpanded = expanded.has(project.id);
          const isDragTarget = dragOverProject === idx && draggingProject !== idx;
          const upload = uploading[project.id];

          return (
            <div
              key={project.id}
              draggable
              onDragStart={() => setDraggingProject(idx)}
              onDragEnter={() => setDragOverProject(idx)}
              onDragEnd={onProjectDragEnd}
              onDragOver={e => e.preventDefault()}
              style={{
                borderBottom: `1px solid ${T.border}`,
                boxShadow: isDragTarget ? `inset 3px 0 0 ${T.text}` : 'none',
                outline: draggingProject === idx ? `2px dashed ${T.muted}` : 'none',
                transition: 'box-shadow 0.1s, outline 0.1s',
              }}
            >
              {/* Project row */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '10px 0', gap: 10 }}>

                {/* Drag grip */}
                <span style={{ color: T.muted, cursor: 'grab', flexShrink: 0, fontSize: 16, userSelect: 'none', lineHeight: 1 }} title="Drag to reorder">⠿</span>

                {/* Name + client — click to expand */}
                <button
                  onClick={() => toggleExpand(project.id)}
                  style={{ flex: 1, minWidth: 0, background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', padding: 0, fontFamily: FONT }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: SZ, color: project.enabled ? T.text : T.muted, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
                      {project.name}
                    </span>
                    {project.images?.length > 0 && (
                      <span style={{ fontSize: SZ, color: T.muted }}>{isExpanded ? '▲' : '▼'}</span>
                    )}
                  </div>
                  <div style={{ fontSize: SZ, letterSpacing: '0.04em', color: T.muted }}>
                    {project.client || '—'}{project.images?.length > 0 ? ` · ${project.images.length} img` : ''}
                  </div>
                </button>

                {/* Controls */}
                <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
                  <button
                    onClick={() => toggleEnabled(project)}
                    style={{ ...S.ghost, padding: '9px 10px', color: project.enabled ? '#2a7a2a' : T.red, borderColor: project.enabled ? '#2a7a2a' : T.red }}
                    title={project.enabled ? 'Visible — click to hide' : 'Hidden — click to show'}
                  >
                    {project.enabled ? 'ON' : 'OFF'}
                  </button>
                  <button onClick={() => setEditing(project)} style={S.ghost}>Edit</button>
                  <button onClick={() => askConfirm('Delete this project and all its images? This cannot be undone.', async () => { setConfirm(null); await deleteProject(project.id); })} style={S.del}>Delete</button>
                </div>
              </div>

              {/* Expanded: image panel */}
              {isExpanded && (
                <div style={{ paddingBottom: 12 }}>
                  {project.images?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 12 }}>
                      {Array.from({ length: Math.ceil(project.images.length / 6) }, (_, rowIdx) =>
                        project.images.slice(rowIdx * 6, rowIdx * 6 + 6)
                      ).map((rowImgs, rowIdx) => (
                      <div key={rowIdx} style={{
                        display: 'flex',
                        flexWrap: isMobile ? 'wrap' : 'nowrap',
                        gap: 6,
                        alignItems: 'flex-start',
                      }}>
                      {rowImgs.map((img, imgIdxInRow) => {
                        const imgIdx = rowIdx * 6 + imgIdxInRow;
                        const isCover = imgIdx === 0;
                        const isDragImgTarget = dragOverImg?.projectId === project.id && dragOverImg?.idx === imgIdx && draggingImg?.idx !== imgIdx;
                        const isDraggingThis = draggingImg?.projectId === project.id && draggingImg?.idx === imgIdx;

                        return (
                          <div
                            key={img.id}
                            draggable
                            onDragStart={() => { if (!libraryDragging) setDraggingImg({ projectId: project.id, idx: imgIdx }); }}
                            onDragEnter={() => { if (draggingImg?.projectId === project.id) setDragOverImg({ projectId: project.id, idx: imgIdx }); }}
                            onDragEnd={() => onImageDragEnd(project.id)}
                            onDragOver={e => e.preventDefault()}
                            style={{
                              flex: '0 0 auto',
                              minWidth: isMobile ? 110 : 0,
                              maxWidth: isMobile ? 110 : 'none',
                              cursor: 'grab',
                              outline: isDraggingThis ? `2px dashed ${T.muted}` : 'none',
                              boxShadow: isDragImgTarget ? `inset 3px 0 0 ${T.text}` : 'none',
                              transition: 'box-shadow 0.1s, outline 0.1s',
                            }}
                          >
                            <div style={{ position: 'relative' }}>
                              <img
                                src={resolveImg(img.src)}
                                alt=""
                                style={{
                                  height: isMobile ? 'auto' : 110,
                                  width: isMobile ? '100%' : 'auto',
                                  display: 'block',
                                  border: `1px solid ${isCover ? T.text : T.border}`,
                                }}
                              />
                              {isCover && (
                                <span style={{
                                  position: 'absolute', top: 5, left: 5,
                                  fontSize: 8, letterSpacing: '0.1em', textTransform: 'uppercase',
                                  fontFamily: FONT, color: '#fff',
                                  background: 'rgba(68,68,67,0.75)',
                                  padding: '2px 5px',
                                  backdropFilter: 'blur(2px)',
                                }}>Cover</span>
                              )}
                            </div>

                            <div style={{ display: 'flex', gap: 3, marginTop: 4 }}>
                              {isCover && (
                                <button
                                  onClick={() => setEditingCrop(img)}
                                  style={{ flex: 1, ...S.ghost, padding: '4px 0', fontSize: 10, textAlign: 'center' }}
                                >Crop</button>
                              )}
                              <button
                                onClick={() => askConfirm('Delete this image? This cannot be undone.', async () => { setConfirm(null); await deleteImage(project.id, img.id); })}
                                style={{ flex: 1, ...S.del, padding: '4px 0', fontSize: 10, textAlign: 'center' }}
                              >✕</button>
                            </div>
                          </div>
                        );
                      })}
                      </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ fontSize: SZ, color: T.muted, letterSpacing: '0.06em', marginBottom: 14 }}>No images yet.</p>
                  )}

                  {/* Library drop zone — visible when a library drag is in progress */}
                  {libraryDragging && (
                    <div
                      onDragOver={e => { e.preventDefault(); setLibDropTarget(project.id); }}
                      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setLibDropTarget(null); }}
                      onDrop={e => handleLibraryDrop(e, project.id)}
                      style={{
                        border: `1px dashed ${libDropTarget === project.id ? T.text : T.border}`,
                        padding: '14px 12px',
                        marginBottom: 12,
                        textAlign: 'center',
                        fontSize: SZ, color: libDropTarget === project.id ? T.text : T.muted,
                        letterSpacing: '0.06em', textTransform: 'uppercase',
                        fontFamily: FONT,
                        transition: 'border-color 0.15s, color 0.15s',
                      }}
                    >
                      {libDropTarget === project.id ? 'Release to add' : 'Drop library image here'}
                    </div>
                  )}

                  <button onClick={() => triggerUpload(project.id)} disabled={!!upload} style={S.ghost}>
                    {upload ? `Uploading ${upload.current} of ${upload.total}...` : '+ Upload Images'}
                  </button>
                </div>
              )}
            </div>
          );
        })
      }

      {confirm && <ConfirmModal message={confirm.message} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}

      {editingCrop && (
        <FocalPointModal
          img={editingCrop}
          onClose={() => setEditingCrop(null)}
          onSaved={async (focal_point) => {
            const updated = await api.projects.updateImage(editingCrop.project_id, editingCrop.id, { focal_point });
            setProjects(prev => prev.map(p =>
              p.id === editingCrop.project_id
                ? { ...p, images: p.images.map(i => i.id === updated.id ? { ...i, focal_point } : i) }
                : p
            ));
          }}
        />
      )}

      {showLibrary && (
        <LibraryPanel
          onClose={() => { setShowLibrary(false); setLibraryDragging(false); }}
          onDragStart={() => setLibraryDragging(true)}
        />
      )}

      {showCreate && (
        <CreateProjectModal
          onClose={() => setShowCreate(false)}
          onCreated={p => { setProjects(prev => [...prev, { ...p, images: p.images || [] }]); setShowCreate(false); }}
        />
      )}
      {editing && (
        <EditProjectModal
          project={editing}
          onClose={() => setEditing(null)}
          onSaved={p => { setProjects(prev => prev.map(x => x.id === p.id ? { ...p, images: x.images } : x)); setEditing(null); }}
        />
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const [section, setSection] = useState('briefs');
  const [clients, setClients] = useState([]);
  const isMobile = useIsMobile();

  useEffect(() => { api.users.list().then(setClients).catch(console.error); }, []);

  return (
    <div style={{ minHeight: '100vh', background: T.bg }}>

      {/* Top nav — matches main website exactly */}
      <nav style={{ backgroundColor: T.bg, borderBottom: `1px solid ${T.border}`, position: 'sticky', top: 0, zIndex: 50 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: isMobile ? '10px 24px' : '10px' }}>
          <a href="/" style={{ color: T.text, fontSize: SZ, letterSpacing: '0.06em', fontWeight: 700, textDecoration: 'none', textTransform: 'uppercase' }}>
            Pierce Bancroft Townsend
          </a>
        </div>
      </nav>

      {/* Tab row — always narrow-centered */}
      <div style={{ maxWidth: 620, margin: '0 auto', padding: '4vh 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 48, marginBottom: 16, borderBottom: `1px solid ${T.border}`, paddingBottom: 10 }}>
          {[{ key: 'briefs', label: 'Briefs' }, { key: 'clients', label: 'Clients' }, { key: 'projects', label: 'Projects' }, { key: 'invoices', label: 'Invoices' }].map(n => (
            <button key={n.key} onClick={() => setSection(n.key)} style={{
              background: 'transparent', border: 'none', padding: 0,
              fontSize: SZ, letterSpacing: '0.06em', textTransform: 'uppercase',
              color: section === n.key ? T.text : T.muted,
              cursor: 'pointer', fontFamily: FONT,
              display: 'flex', alignItems: 'center',
            }}>
              {section === n.key && <Dot />}
              {n.label}
            </button>
          ))}
        </div>
      </div>

      {/* Section content — Projects is full-width, others stay narrow */}
      {section === 'projects' ? (
        <div style={{ padding: isMobile ? '0 24px 40px' : '0 120px 40px' }}>
          <ProjectsSection />
        </div>
      ) : (
        <div style={{ maxWidth: 620, margin: '0 auto', padding: '0 24px 40px' }}>
          {section === 'briefs' && <BriefsSection clients={clients} />}
          {section === 'clients' && <ClientsSection />}
          {section === 'invoices' && <InvoicePage />}
        </div>
      )}
    </div>
  );
}
